import { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Button,
  Tooltip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
  Collapse,
  IconButton,
} from '@mui/material';
import { ChevronDown, Database, Calendar, Check, Plus, RefreshCw, ChevronRight } from 'lucide-react';
import { useRawDataSources, usePMNData, useNode, useCreateNode, useCreateEdge, useUpdateNode } from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import { useQueryClient } from '@tanstack/react-query';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { useToast } from '../../contexts/ToastContext';

interface SourcesSectionProps {
  nodeId: string;
  nodeType: string;
}

interface RawDataRecord {
  id: string;
  source_system: string;
  object_type: string;
  data: Record<string, unknown>;
  synced_at: string;
}

interface ExtractedField {
  name: string;
  value: unknown;
  displayValue: string;
  isBaseProp: boolean;
}

type FieldStatus = 'applied' | 'available' | 'different';

interface ComparedField extends ExtractedField {
  status: FieldStatus;
  currentValue?: unknown;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getSourceLabel(source: string): string {
  const labels: Record<string, string> = {
    hunter: 'Hunter.io',
    perplexity: 'Perplexity AI',
    salesforce: 'Salesforce',
    hubspot: 'HubSpot',
  };
  return labels[source] || source;
}

function getSourceColor(source: string): 'primary' | 'secondary' | 'success' | 'warning' | 'info' {
  const colors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
    hunter: 'warning',
    perplexity: 'info',
    salesforce: 'primary',
    hubspot: 'secondary',
  };
  return colors[source] || 'primary';
}

function formatDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    if (value.length === 0) return '(empty)';
    // If array contains objects, show count instead of [object Object]
    if (typeof value[0] === 'object' && value[0] !== null) {
      return `${value.length} items`;
    }
    return value.join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((acc: unknown, part: string) => {
    if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

function extractHunterOrgFields(data: Record<string, unknown>): ExtractedField[] {
  const fields: ExtractedField[] = [];
  const company = data.company_response as Record<string, unknown> | null;
  if (!company) return fields;

  const basePropMappings: Array<{ path: string; name: string }> = [
    { path: 'name', name: 'name' },
    { path: 'domain', name: 'website' },
    { path: 'category.industry', name: 'industry' },
  ];
  const propertyMappings: Array<{ path: string; name: string }> = [
    { path: 'logo', name: 'logo_url' },
    { path: 'description', name: 'description' },
    { path: 'legalName', name: 'legal_name' },
    { path: 'foundedYear', name: 'founded_year' },
    { path: 'metrics.employees', name: 'employee_count' },
    { path: 'metrics.annualRevenue', name: 'annual_revenue' },
    { path: 'linkedin.handle', name: 'linkedin_url' },
    { path: 'twitter.handle', name: 'twitter_url' },
    { path: 'facebook.handle', name: 'facebook_url' },
    { path: 'youtube.handle', name: 'youtube_url' },
    { path: 'site.phoneNumbers', name: 'phone_numbers' },
    { path: 'site.emailAddresses', name: 'email_addresses' },
    { path: 'category.sector', name: 'sector' },
    { path: 'category.subIndustry', name: 'sub_industry' },
    { path: 'tags', name: 'tags' },
    { path: 'tech', name: 'tech_stack' },
    { path: 'companyType', name: 'company_type' },
    { path: 'emailProvider', name: 'email_provider' },
  ];

  for (const mapping of basePropMappings) {
    const value = getNestedValue(company, mapping.path);
    if (value !== undefined && value !== null && value !== '') {
      fields.push({ name: mapping.name, value, displayValue: formatDisplayValue(value), isBaseProp: true });
    }
  }
  for (const mapping of propertyMappings) {
    let value = getNestedValue(company, mapping.path);
    if (value !== undefined && value !== null && value !== '') {
      if (mapping.name.endsWith('_url') && typeof value === 'string' && !value.startsWith('http')) {
        const prefix = mapping.name.includes('linkedin')
          ? 'https://linkedin.com/company/'
          : mapping.name.includes('twitter')
            ? 'https://twitter.com/'
            : mapping.name.includes('facebook')
              ? 'https://facebook.com/'
              : mapping.name.includes('youtube')
                ? 'https://youtube.com/'
                : 'https://';
        value = prefix + value;
      }
      fields.push({ name: mapping.name, value, displayValue: formatDisplayValue(value), isBaseProp: false });
    }
  }
  return fields;
}

function extractPerplexityOrgFields(data: Record<string, unknown>): ExtractedField[] {
  const fields: ExtractedField[] = [];
  const rawResponse = data.raw_response as Record<string, unknown> | null;
  if (!rawResponse) return fields;

  const basePropKeys = ['name', 'website', 'industry'];
  const skipKeys = ['cost', 'tokens', 'entity_id', 'entity_name', 'entity_type', 'raw_response'];

  for (const [key, value] of Object.entries(rawResponse)) {
    if (skipKeys.includes(key)) continue;
    if (value === null || value === undefined || value === '' || value === 'Not available') continue;
    const isBaseProp = basePropKeys.includes(key);
    fields.push({ name: key, value, displayValue: formatDisplayValue(value), isBaseProp });
  }
  return fields;
}

function extractHunterPersonFields(data: Record<string, unknown>, personEmail: string): ExtractedField[] {
  const fields: ExtractedField[] = [];
  const domainSearch = data.domain_search_response as { emails?: Array<Record<string, unknown>> } | null;
  if (!domainSearch?.emails) return fields;

  const personData = domainSearch.emails.find((e) => e.value === personEmail);
  if (!personData) return fields;

  const basePropMappings: Array<{ path: string; name: string }> = [
    { path: 'value', name: 'email' },
    { path: 'seniority', name: 'seniority' },
    { path: 'department', name: 'role_type' },
  ];
  const propertyMappings: Array<{ path: string; name: string }> = [
    { path: 'linkedin', name: 'linkedin_url' },
    { path: 'twitter', name: 'twitter_url' },
    { path: 'phone_number', name: 'phone' },
    { path: 'position', name: 'title' },
    { path: 'position_raw', name: 'title_full' },
    { path: 'confidence', name: 'hunter_confidence' },
  ];

  for (const mapping of basePropMappings) {
    const value = personData[mapping.path];
    if (value !== undefined && value !== null && value !== '') {
      fields.push({ name: mapping.name, value, displayValue: formatDisplayValue(value), isBaseProp: true });
    }
  }
  for (const mapping of propertyMappings) {
    let value = personData[mapping.path];
    if (value !== undefined && value !== null && value !== '') {
      if (mapping.name === 'twitter_url' && typeof value === 'string' && !value.startsWith('http')) {
        value = `https://twitter.com/${value}`;
      }
      fields.push({ name: mapping.name, value, displayValue: formatDisplayValue(value), isBaseProp: false });
    }
  }
  return fields;
}

function extractFieldsFromSource(source: RawDataRecord, nodeType: string, nodeProps?: Record<string, unknown>): ExtractedField[] {
  if (nodeType === 'Organization') {
    if (source.source_system === 'hunter') {
      return extractHunterOrgFields(source.data);
    } else if (source.source_system === 'perplexity') {
      return extractPerplexityOrgFields(source.data);
    }
  } else if (nodeType === 'Person') {
    if (source.source_system === 'hunter' && nodeProps?.email) {
      return extractHunterPersonFields(source.data, nodeProps.email as string);
    }
  }
  return [];
}

function compareFields(
  sourceFields: ExtractedField[],
  nodeProps: Record<string, unknown>,
  pmnData:
    | {
        properties: Array<{ node: { props: Record<string, unknown> } }>;
        metrics: Array<{ node: { props: Record<string, unknown> } }>;
        narrative: Array<{ node: { props: Record<string, unknown> } }>;
      }
    | null
    | undefined
): ComparedField[] {
  const existingPMN = new Map<string, unknown>();
  if (pmnData) {
    for (const p of pmnData.properties || []) {
      const props = p.node.props as { name?: string; value?: unknown };
      if (props.name) existingPMN.set(props.name, props.value);
    }
    for (const m of pmnData.metrics || []) {
      const props = m.node.props as { name?: string; value?: unknown };
      if (props.name) existingPMN.set(props.name, props.value);
    }
    for (const n of pmnData.narrative || []) {
      const props = n.node.props as { name?: string; content?: unknown };
      if (props.name) existingPMN.set(props.name, props.content);
    }
  }

  return sourceFields.map((field) => {
    let currentValue: unknown;
    let status: FieldStatus = 'available';

    if (field.isBaseProp) {
      currentValue = nodeProps[field.name];
    } else {
      currentValue = existingPMN.get(field.name);
    }

    if (currentValue !== undefined && currentValue !== null) {
      const currentStr = formatDisplayValue(currentValue);
      const sourceStr = formatDisplayValue(field.value);
      status = currentStr === sourceStr ? 'applied' : 'different';
    }

    return { ...field, status, currentValue };
  });
}

function ArrayItemRow({ item, idx, fieldName, nodeId, sourceId, onApplied }: { item: unknown; idx: number; fieldName: string; nodeId: string; sourceId: string; onApplied: () => void }) {
  const { data: user } = useCurrentUser();
  const createNode = useCreateNode();
  const createEdge = useCreateEdge();
  const { showToast } = useToast();
  const [itemAnchorEl, setItemAnchorEl] = useState<null | HTMLElement>(null);
  const [itemApplying, setItemApplying] = useState(false);
  const itemMenuOpen = Boolean(itemAnchorEl);

  const handleItemMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setItemAnchorEl(event.currentTarget);
  };

  const handleItemMenuClose = () => {
    setItemAnchorEl(null);
  };

  const handleAddItem = async (pmnType: 'Property' | 'Metric' | 'Narrative') => {
    if (!user?.tenant_id) return;
    handleItemMenuClose();
    setItemApplying(true);
    try {
      const edgeType = pmnType === 'Property' ? 'HAS_PROPERTY' : pmnType === 'Metric' ? 'HAS_METRIC' : 'HAS_NARRATIVE';

      // Generate name for this item
      const itemName = typeof item === 'object' && item !== null && 'title' in item ? `${fieldName}_${(item as { title: string }).title}` : `${fieldName}_${idx + 1}`;

      let nodeProps: Record<string, unknown>;
      if (pmnType === 'Narrative') {
        nodeProps = {
          name: itemName,
          content: typeof item === 'string' ? item : JSON.stringify(item, null, 2),
          narrative_type: 'enrichment',
          source: 'enrichment',
          source_raw_data_id: sourceId,
        };
      } else if (pmnType === 'Metric') {
        nodeProps = { name: itemName, value: item, metric_type: 'enrichment', source: 'enrichment', source_raw_data_id: sourceId };
      } else {
        nodeProps = {
          name: itemName,
          value: typeof item === 'string' ? item : JSON.stringify(item),
          property_type: 'enrichment',
          source: 'enrichment',
          source_raw_data_id: sourceId,
        };
      }

      const pmnNode = await createNode.mutateAsync({
        type: pmnType,
        props: nodeProps,
        tenant_id: user.tenant_id,
      });
      await createEdge.mutateAsync({ type: edgeType, src: nodeId, dst: pmnNode.id, props: {}, tenant_id: user.tenant_id });
      showToast(`Added ${pmnType.toLowerCase()}: ${itemName}`, 'success');
      onApplied();
    } catch (err) {
      showToast(`Failed to add item: ${(err as Error).message}`, 'error');
    } finally {
      setItemApplying(false);
    }
  };

  const itemDisplay = typeof item === 'object' && item !== null ? JSON.stringify(item, null, 2) : String(item);

  return (
    <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
      <TableCell sx={{ p: 1, pl: 4, width: 60 }}>
        <Typography variant="caption" color="text.secondary">
          #{idx + 1}
        </Typography>
      </TableCell>
      <TableCell sx={{ p: 1 }}>
        <Box
          component="pre"
          sx={{
            fontSize: '0.7rem',
            fontFamily: 'monospace',
            m: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 150,
            overflow: 'auto',
          }}
        >
          {itemDisplay}
        </Box>
      </TableCell>
      <TableCell sx={{ width: 100, p: 1 }}>
        <Button size="small" variant="outlined" onClick={handleItemMenuOpen} disabled={itemApplying}>
          {itemApplying ? '...' : 'Add ▾'}
        </Button>
        <Menu anchorEl={itemAnchorEl} open={itemMenuOpen} onClose={handleItemMenuClose}>
          <MenuItem onClick={() => handleAddItem('Property')}>As Property</MenuItem>
          <MenuItem onClick={() => handleAddItem('Metric')}>As Metric</MenuItem>
          <MenuItem onClick={() => handleAddItem('Narrative')}>As Narrative</MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}

function FieldRow({ field, nodeId, nodeType, sourceId, onApplied }: { field: ComparedField; nodeId: string; nodeType: string; sourceId: string; onApplied: () => void }) {
  const { data: user } = useCurrentUser();
  const createNode = useCreateNode();
  const createEdge = useCreateEdge();
  const updateNode = useUpdateNode();
  const { showToast } = useToast();
  const [isApplying, setIsApplying] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState(false);
  const menuOpen = Boolean(anchorEl);

  // Check if value is complex (array or object) and needs preview
  const isComplex = Array.isArray(field.value) || (typeof field.value === 'object' && field.value !== null);
  const previewContent = isComplex ? JSON.stringify(field.value, null, 2) : null;

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddPMN = async (pmnType: 'Property' | 'Metric' | 'Narrative') => {
    if (!user?.tenant_id) return;
    handleMenuClose();
    setIsApplying(true);
    try {
      const edgeType = pmnType === 'Property' ? 'HAS_PROPERTY' : pmnType === 'Metric' ? 'HAS_METRIC' : 'HAS_NARRATIVE';

      let nodeProps: Record<string, unknown>;
      if (pmnType === 'Narrative') {
        nodeProps = { name: field.name, content: field.value, narrative_type: 'enrichment', source: 'enrichment', source_raw_data_id: sourceId };
      } else if (pmnType === 'Metric') {
        nodeProps = { name: field.name, value: field.value, metric_type: 'enrichment', source: 'enrichment', source_raw_data_id: sourceId };
      } else {
        nodeProps = { name: field.name, value: field.value, property_type: 'enrichment', source: 'enrichment', source_raw_data_id: sourceId };
      }

      const pmnNode = await createNode.mutateAsync({
        type: pmnType,
        props: nodeProps,
        tenant_id: user.tenant_id,
      });
      await createEdge.mutateAsync({ type: edgeType, src: nodeId, dst: pmnNode.id, props: {}, tenant_id: user.tenant_id });
      showToast(`Added ${pmnType.toLowerCase()}: ${field.name}`, 'success');
      onApplied();
    } catch (err) {
      showToast(`Failed to add ${pmnType.toLowerCase()}: ${(err as Error).message}`, 'error');
    } finally {
      setIsApplying(false);
    }
  };

  const handleUpdateBaseProp = async () => {
    setIsApplying(true);
    try {
      await updateNode.mutateAsync({ id: nodeId, updates: { props: { [field.name]: field.value } }, nodeType });
      showToast(`Updated ${field.name}`, 'success');
      onApplied();
    } catch (err) {
      showToast(`Failed to update: ${(err as Error).message}`, 'error');
    } finally {
      setIsApplying(false);
    }
  };

  const statusIcon = field.status === 'applied' ? <Check size={14} color="green" /> : field.status === 'different' ? <RefreshCw size={14} color="orange" /> : <Plus size={14} color="gray" />;
  const statusColor = field.status === 'applied' ? 'success.main' : field.status === 'different' ? 'warning.main' : 'text.secondary';

  return (
    <>
      <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
        <TableCell sx={{ width: 30, p: 1 }}>
          <Tooltip title={field.status === 'applied' ? 'Applied' : field.status === 'different' ? 'Different value' : 'Not applied'}>
            <Box sx={{ color: statusColor }}>{statusIcon}</Box>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ p: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isComplex && (
              <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0 }}>
                <ChevronRight size={16} style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
              </IconButton>
            )}
            <Typography variant="body2" fontWeight={500}>
              {field.name}
            </Typography>
            {field.isBaseProp && <Chip label="baseProps" size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
          </Box>
        </TableCell>
        <TableCell sx={{ p: 1, maxWidth: 300 }}>
          <Typography variant="body2" noWrap title={field.displayValue}>
            {field.displayValue.length > 50 ? field.displayValue.slice(0, 50) + '...' : field.displayValue}
          </Typography>
          {field.status === 'different' && field.currentValue !== undefined && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Current: {formatDisplayValue(field.currentValue).slice(0, 30)}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ width: 100, p: 1 }}>
          {field.status === 'available' &&
            (field.isBaseProp ? (
              <Button size="small" variant="outlined" onClick={handleUpdateBaseProp} disabled={isApplying}>
                {isApplying ? '...' : 'Add'}
              </Button>
            ) : (
              <>
                <Button size="small" variant="outlined" onClick={handleMenuOpen} disabled={isApplying}>
                  {isApplying ? '...' : 'Add ▾'}
                </Button>
                <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
                  <MenuItem onClick={() => handleAddPMN('Property')}>As Property</MenuItem>
                  <MenuItem onClick={() => handleAddPMN('Metric')}>As Metric</MenuItem>
                  <MenuItem onClick={() => handleAddPMN('Narrative')}>As Narrative</MenuItem>
                </Menu>
              </>
            ))}
          {field.status === 'different' &&
            (field.isBaseProp ? (
              <Button size="small" variant="outlined" color="warning" onClick={handleUpdateBaseProp} disabled={isApplying}>
                {isApplying ? '...' : 'Update'}
              </Button>
            ) : (
              <>
                <Button size="small" variant="outlined" color="warning" onClick={handleMenuOpen} disabled={isApplying}>
                  {isApplying ? '...' : 'Update ▾'}
                </Button>
                <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
                  <MenuItem onClick={() => handleAddPMN('Property')}>As Property</MenuItem>
                  <MenuItem onClick={() => handleAddPMN('Metric')}>As Metric</MenuItem>
                  <MenuItem onClick={() => handleAddPMN('Narrative')}>As Narrative</MenuItem>
                </Menu>
              </>
            ))}
        </TableCell>
      </TableRow>
      {isComplex && Array.isArray(field.value) && (
        <TableRow>
          <TableCell colSpan={4} sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}>
            <Collapse in={expanded}>
              <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Items ({field.value.length}):
                </Typography>
                <Table size="small">
                  <TableBody>
                    {field.value.map((item, idx) => (
                      <ArrayItemRow key={idx} item={item} idx={idx} fieldName={field.name} nodeId={nodeId} sourceId={sourceId} onApplied={onApplied} />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
      {isComplex && !Array.isArray(field.value) && (
        <TableRow>
          <TableCell colSpan={4} sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}>
            <Collapse in={expanded}>
              <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Preview:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    bgcolor: 'background.paper',
                    p: 1.5,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 300,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  {previewContent}
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function SourceAccordion({
  source,
  nodeId,
  nodeType,
  nodeProps,
  pmnData,
}: {
  source: RawDataRecord;
  nodeId: string;
  nodeType: string;
  nodeProps: Record<string, unknown>;
  pmnData: ReturnType<typeof usePMNData>['data'];
}) {
  const queryClient = useQueryClient();
  const extractedFields = useMemo(() => extractFieldsFromSource(source, nodeType, nodeProps), [source, nodeType, nodeProps]);
  const comparedFields = useMemo(() => compareFields(extractedFields, nodeProps, pmnData), [extractedFields, nodeProps, pmnData]);

  const appliedCount = comparedFields.filter((f) => f.status === 'applied').length;
  const availableCount = comparedFields.filter((f) => f.status === 'available').length;
  const differentCount = comparedFields.filter((f) => f.status === 'different').length;

  const handleApplied = () => {
    queryClient.invalidateQueries({ queryKey: ['node', nodeId] });
    queryClient.invalidateQueries({ queryKey: ['pmn-data', nodeId] });
  };

  return (
    <Accordion defaultExpanded={availableCount > 0 || differentCount > 0}>
      <AccordionSummary expandIcon={<ChevronDown size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Chip label={getSourceLabel(source.source_system)} color={getSourceColor(source.source_system)} size="small" />
          <Box sx={{ display: 'flex', gap: 1 }}>
            {appliedCount > 0 && <Chip label={`${appliedCount} applied`} size="small" color="success" variant="outlined" />}
            {availableCount > 0 && <Chip label={`${availableCount} available`} size="small" color="primary" variant="outlined" />}
            {differentCount > 0 && <Chip label={`${differentCount} different`} size="small" color="warning" variant="outlined" />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', mr: 2 }}>
            <Calendar size={14} />
            <Typography variant="caption" color="text.secondary">
              {formatDate(source.synced_at)}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {comparedFields.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No extractable fields found in this source
          </Typography>
        ) : (
          <Table size="small">
            <TableBody>
              {comparedFields.map((field, idx) => (
                <FieldRow key={`${field.name}-${idx}`} field={field} nodeId={nodeId} nodeType={nodeType} sourceId={source.id} onApplied={handleApplied} />
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export function SourcesSection({ nodeId, nodeType }: SourcesSectionProps) {
  const { data: sources, isLoading: sourcesLoading } = useRawDataSources(nodeId);
  const { data: node, isLoading: nodeLoading } = useNode(nodeId);
  const { data: pmnData, isLoading: pmnLoading } = usePMNData(nodeId);

  const isLoading = sourcesLoading || nodeLoading || pmnLoading;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!sources || sources.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Database size={ICON_SIZES.xl} strokeWidth={ICON_STROKE} style={{ opacity: 0.3, marginBottom: 8 }} />
        <Typography variant="body1" color="text.secondary">
          No data sources linked to this {nodeType.toLowerCase()}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Enrich this entity to see source data here
        </Typography>
      </Paper>
    );
  }

  const nodeProps = (node?.props as Record<string, unknown>) || {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">Data Sources ({sources.length})</Typography>
      {sources.map((source: RawDataRecord) => (
        <SourceAccordion key={source.id} source={source} nodeId={nodeId} nodeType={nodeType} nodeProps={nodeProps} pmnData={pmnData} />
      ))}
    </Box>
  );
}
